// FLAG:useDocuAi — pure transient/permanent classifier for upload failures. Leaf module (no
// store/FileService imports) so it stays unit-testable in isolation, same shape as
// classifyBatchUploads.
//
// Consumed only by the receipt upload path's retry policy (see retryUpload / fileService). The
// legacy path never calls it: every failure there stays terminal on the first attempt exactly as
// before.
//
// The input is the axios error object that `onUploadError` already destructures
// (`{ response }` with `response.data.message` / `response.data.meta`), so the shapes below are
// the ones the API actually produces — not invented ones.

import { errorCodes, subscriptionLimits } from '@/modules/base/util/constants'

export type UploadErrorClass = 'transient' | 'permanent'

type AxiosLikeError = {
  code?: string,
  message?: string,
  response?: {
    status?: number,
    data?: {
      message?: string,
      meta?: Record<string, unknown>,
    },
  },
}

// Retryable: the request never reached a decision, or the server explicitly said "later".
// 408 Request Timeout and 425 Too Early are included for completeness; 429 is the DMS/API rate
// limit; 502/503/504 are the gateway/restart window that a mass upload is most likely to hit.
const TRANSIENT_STATUS = new Set([408, 425, 429, 502, 503, 504])

// Deliberately NOT retried:
//   • 400/422 — the payload is wrong; a retry sends the identical payload.
//   • 401     — session drop. Retrying without re-auth just burns the budget; the user must
//               re-login. (This is the failure mode from receipt-upload-failure-endless-spinner.)
//   • 403     — permission/OpenFGA. Stable.
//   • 404/409 — the upload token is gone or the state moved on.
//   • 500     — see below.
const PERMANENT_STATUS = new Set([400, 401, 403, 404, 409, 413, 415, 422, 500])

const hasNotAllowedCode = (value: unknown): boolean =>
  Array.isArray(value) && value.some((item) => (item as { code?: string })?.code === errorCodes.NOT_ALLOWED)

// Mirrors onUploadError's own detection so the classifier and the message it renders agree.
const isFileTypeRejected = (meta: Record<string, unknown>, message: string): boolean => {
  if (hasNotAllowedCode(meta?.mimetype)) return true
  if (Object.entries(meta ?? {}).some(([key, value]) => key.toLowerCase().includes('extension') && hasNotAllowedCode(value))) return true
  const lowered = message.toLowerCase()
  return lowered.includes('extension') && lowered.includes('not allowed')
}

// Quota/plan limits open the upgrade modal in onUploadError — retrying cannot change the outcome.
const isSubscriptionLimit = (meta: Record<string, unknown>): boolean => Boolean(
  meta?.[subscriptionLimits.STORAGE_SPACE]
  || meta?.[subscriptionLimits.DOCUMENT_COUNT]
  || meta?.[subscriptionLimits.DOCUMENT_COUNT_YEAR],
)

export const classifyUploadError = (error: AxiosLikeError): UploadErrorClass => {
  const meta = error?.response?.data?.meta ?? {}
  const message = error?.response?.data?.message ?? error?.message ?? ''

  // A rejected file stays rejected regardless of status code — check the payload before the code.
  if (isFileTypeRejected(meta, message)) return 'permanent'
  if (isSubscriptionLimit(meta)) return 'permanent'

  const status = error?.response?.status

  // No HTTP response at all: connection refused, DNS failure, socket reset, or our own axios
  // timeout firing (ECONNABORTED / ERR_CANCELED). This is the single most common mass-upload
  // failure and the one most worth retrying.
  if (status === undefined) return 'transient'

  if (TRANSIENT_STATUS.has(status)) return 'transient'
  if (PERMANENT_STATUS.has(status)) return 'permanent'

  // Unknown 5xx (501, 507, …): treat as permanent rather than burning the retry budget on a
  // server condition we have no evidence is temporary.
  return 'permanent'
}

// 500 is deliberately PERMANENT.
//
// This is the one classification that plan task T1 (the live repro) was supposed to settle and
// did not — T1 was deferred, so there is no evidence that the mass-upload 500s are load-induced
// and succeed on retry. Retrying a genuine server bug three times per file just multiplies the
// load that likely caused it. If a repro later shows these 500s ARE transient, move 500 from
// PERMANENT_STATUS to TRANSIENT_STATUS — that is the whole change.
export const RETRY_CLASSIFICATION_OPEN_QUESTION = 'HTTP 500 treated as permanent pending the T1 repro'
